import { STRINGS } from '@/config/audio';
import { centsBetween, indexToStringNumber, midiToFrequency, STANDARD_TUNING_MIDI } from '@/data/tuning';
import type { StringReport, StringState } from '@/types/audio';
import type { Chord, StringNumber } from '@/types/music';
import { amplitudeToDb, clamp01, normalizeRange } from '../dsp/stats';
import { binRangeAround, localBackground, peakIn } from '../dsp/spectrum';

/**
 * Per-string analysis with an explicit honesty gate.
 *
 * Guitar partials collide by construction. In an open C chord the 5th string
 * (C3, 130.81 Hz) has a 3rd harmonic at 392.4 Hz and the open 3rd string (G3,
 * 196.00 Hz) has a 2nd harmonic at 392.0 Hz — 0.1 % apart. No FFT length
 * separates them, so any claim built on that bin is guesswork.
 *
 * Therefore, before measuring anything, we drop every partial that lands within
 * `partialCollisionCents` of a partial belonging to another string of the same
 * chord. A string with no surviving partial is reported as `indeterminate` and
 * the UI says nothing about it. Typically three to five of six strings survive.
 */

export interface StringAnalysisInput {
  magnitudes: Float32Array;
  /** Sample rate of the spectrum (the decimated rate). */
  sampleRate: number;
  fftSize: number;
  /** Calibrated noise floor as a linear magnitude. */
  noiseFloor: number;
}

interface PartialPlan {
  string: StringNumber;
  frequency: number;
  /** Harmonic index, 0 = fundamental. */
  harmonic: number;
}

export interface StringPlan {
  chordId: string;
  /** Partials that survived the collision mask, for strings that should sound. */
  expected: Map<StringNumber, PartialPlan[]>;
  /** Same, for strings the player is supposed to mute. */
  muted: Map<StringNumber, PartialPlan[]>;
  /** Strings with no usable partial at all. */
  indeterminate: StringNumber[];
}

/** Build the collision mask for a chord. Pure, cached by the caller. */
export function buildStringPlan(chord: Chord): StringPlan {
  const sounding: PartialPlan[] = [];
  const mutedRaw: PartialPlan[] = [];

  for (let index = 0; index < chord.frets.length; index++) {
    const fret = chord.frets[index];
    const stringNumber = indexToStringNumber(index);
    if (fret === null || fret === undefined) {
      const f0 = midiToFrequency(STANDARD_TUNING_MIDI[index]);
      for (let h = 0; h < STRINGS.partials; h++) {
        mutedRaw.push({ string: stringNumber, frequency: f0 * (h + 1), harmonic: h });
      }
      continue;
    }
    const f0 = midiToFrequency(STANDARD_TUNING_MIDI[index] + fret);
    for (let h = 0; h < STRINGS.partials; h++) {
      sounding.push({ string: stringNumber, frequency: f0 * (h + 1), harmonic: h });
    }
  }

  const expected = new Map<StringNumber, PartialPlan[]>();
  for (const partial of sounding) {
    if (collidesWithOther(partial, sounding)) continue;
    push(expected, partial);
  }

  // A muted string is detectable only where it does not overlap anything the
  // chord is supposed to produce — and not where it overlaps another muted
  // string either, or we could not tell which one rang.
  const muted = new Map<StringNumber, PartialPlan[]>();
  for (const partial of mutedRaw) {
    if (collidesWithAny(partial, sounding)) continue;
    if (collidesWithOther(partial, mutedRaw)) continue;
    push(muted, partial);
  }

  const indeterminate: StringNumber[] = [];
  for (let index = 0; index < chord.frets.length; index++) {
    const stringNumber = indexToStringNumber(index);
    const isMuted = chord.frets[index] === null;
    const map = isMuted ? muted : expected;
    if (!map.has(stringNumber)) indeterminate.push(stringNumber);
  }

  return { chordId: chord.id, expected, muted, indeterminate };
}

function push(map: Map<StringNumber, PartialPlan[]>, partial: PartialPlan): void {
  const list = map.get(partial.string);
  if (list) list.push(partial);
  else map.set(partial.string, [partial]);
}

function collidesWithOther(partial: PartialPlan, all: PartialPlan[]): boolean {
  for (const other of all) {
    if (other.string === partial.string) continue;
    if (Math.abs(centsBetween(partial.frequency, other.frequency)) < STRINGS.partialCollisionCents) {
      return true;
    }
  }
  return false;
}

function collidesWithAny(partial: PartialPlan, all: PartialPlan[]): boolean {
  for (const other of all) {
    if (Math.abs(centsBetween(partial.frequency, other.frequency)) < STRINGS.partialCollisionCents) {
      return true;
    }
  }
  return false;
}

/**
 * Measure every reportable string.
 *
 * Two kinds of string are deliberately absent from the result:
 * a muted string that stayed quiet (there is nothing to say about a string the
 * player correctly avoided), and any string with no usable partial, which is
 * reported explicitly as `indeterminate` instead.
 */
export function analyzeStrings(plan: StringPlan, input: StringAnalysisInput): StringReport[] {
  const reports: StringReport[] = [];

  for (const [stringNumber, partials] of plan.expected) {
    const measured = measure(partials, input);
    if (!measured) {
      reports.push(indeterminateReport(stringNumber));
      continue;
    }
    const threshold = measured.lowest ? STRINGS.presentSnrDb + STRINGS.lowStringExtraDb : STRINGS.presentSnrDb;
    let state: StringState;
    if (measured.snrDb >= threshold) state = 'clear';
    else if (measured.snrDb >= STRINGS.silentSnrDb) state = 'weak';
    else state = 'silent';

    reports.push({
      string: stringNumber,
      state,
      confidence: stateConfidence(measured.snrDb, threshold, measured.usable, measured.lowest),
      snrDb: measured.snrDb,
      usablePartials: measured.usable,
    });
  }

  for (const [stringNumber, partials] of plan.muted) {
    const measured = measure(partials, input);
    if (!measured) {
      reports.push(indeterminateReport(stringNumber));
      continue;
    }
    if (measured.snrDb >= STRINGS.unwantedSnrDb) {
      reports.push({
        string: stringNumber,
        state: 'unwanted',
        confidence: stateConfidence(measured.snrDb, STRINGS.unwantedSnrDb, measured.usable, measured.lowest),
        snrDb: measured.snrDb,
        usablePartials: measured.usable,
      });
    }
    // A quiet muted string is simply correct — nothing to report.
  }

  for (const stringNumber of plan.indeterminate) {
    reports.push(indeterminateReport(stringNumber));
  }

  return reports.sort((a, b) => a.string - b.string);
}

function indeterminateReport(string: StringNumber): StringReport {
  return { string, state: 'indeterminate', confidence: 0, snrDb: -Infinity, usablePartials: 0 };
}

interface Measurement {
  snrDb: number;
  usable: number;
  /** True when every usable partial sits in the range phone mics roll off. */
  lowest: boolean;
}

function measure(partials: PartialPlan[], input: StringAnalysisInput): Measurement | null {
  const { magnitudes, sampleRate, fftSize, noiseFloor } = input;
  const binCount = magnitudes.length;
  const nyquist = sampleRate / 2;

  let best = -Infinity;
  let usable = 0;
  let allLow = true;

  for (const partial of partials) {
    if (partial.frequency > nyquist * 0.9) continue;
    const range = binRangeAround(partial.frequency, STRINGS.searchCents, sampleRate, fftSize, binCount);
    const peak = peakIn(magnitudes, range);
    // Compare against whichever is higher: the calibrated floor, or the local
    // spectral shelf. A partial sitting on top of a broadband hump is not a
    // peak, and treating it as one is how phantom strings get reported.
    const background = Math.max(noiseFloor, localBackground(magnitudes, range, STRINGS.backgroundSpread));
    const snrDb = amplitudeToDb(peak) - amplitudeToDb(background);
    usable++;
    if (partial.frequency > STRINGS.lowStringMaxHz) allLow = false;
    if (snrDb > best) best = snrDb;
  }

  if (usable === 0) return null;
  return { snrDb: best, usable, lowest: allLow };
}

function stateConfidence(snrDb: number, threshold: number, usable: number, lowOnly: boolean): number {
  // Distance from the decision boundary: a value right on the threshold is a
  // coin flip and must not be announced.
  const distance = normalizeRange(Math.abs(snrDb - threshold), 1, 8);
  const evidence = normalizeRange(usable, 1, 3);
  const penalty = lowOnly ? STRINGS.lowOnlyConfidencePenalty : 1;
  return clamp01((0.6 * distance + 0.4 * evidence) * penalty);
}

/**
 * Clarity over *determinable* strings only, as a percentage.
 *
 * Indeterminate strings are excluded from both numerator and denominator: a
 * string we cannot hear must not count as either success or failure.
 */
export function clarityScore(reports: StringReport[]): number {
  let sum = 0;
  let count = 0;
  let unwanted = 0;

  for (const report of reports) {
    if (report.state === 'indeterminate') continue;
    if (report.state === 'unwanted') {
      unwanted++;
      continue;
    }
    count++;
    if (report.state === 'clear') sum += 1;
    else if (report.state === 'weak') sum += STRINGS.weakStringCredit;
  }

  if (count === 0) return 0;
  const base = sum / count;
  const penalty = Math.min(1, unwanted * STRINGS.unwantedPenalty);
  return Math.round(clamp01(base * (1 - penalty)) * 100);
}
